import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  X, Edit2, GitBranch, Pencil, Plus, Camera, Trash2, Save,
  MessageSquare, CheckCircle, Clock, AlertTriangle, Wallet, Send,
  Download, Copy, Check, AlertCircle, CreditCard, Truck, Calendar,
  Zap, FileText, ArrowRight, RotateCw, Mail, Tag
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { PHOTOBOOTH_DEVIS_OPTIONS, photoboothPrintsCost } from '../../config/photoboothPacks';
import AdminOrderTimeline from '../AdminOrderTimeline';
import { RelanceComposer } from './RelanceComposer';
import { STATUTS } from './statuts';
import { openProtectedDoc } from '../../utils/docLinks';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Modal éditeur de devis
export default function DevisEditor({ quote, onClose, onSaved, onReload }) {
  const [editedItems, setEditedItems] = useState(quote.items || []);
  const [remisePct, setRemisePct] = useState(quote.remise_globale_pct || 0);
  const [noteTexte, setNoteTexte] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showAddPhotobooth, setShowAddPhotobooth] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingPayLink, setSendingPayLink] = useState(false);
  const [showCancelRefund, setShowCancelRefund] = useState(false);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [cancelSubject, setCancelSubject] = useState('');
  const [cancelBody, setCancelBody] = useState('');
  const [cancelSending, setCancelSending] = useState(false);
  const [compEnabled, setCompEnabled] = useState(true);
  const [compMontant, setCompMontant] = useState(15);
  const [photoCount, setPhotoCount] = useState('');
  const [montantPaye, setMontantPaye] = useState(0);
  // ── Onglet actif : 'edit' = DevisEditor | 'timeline' = Cycle de vie ───────
  const [activeDevisTab, setActiveDevisTab] = useState('edit');
  const [editingClient, setEditingClient] = useState(false);
  const [clientForm, setClientForm] = useState({});
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${BACKEND}/api/products?limit=100`, { headers: H })
      .then(r => r.json()).then(d => setAllProducts(d.products || []));
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!quote?.id) return;
    fetch(`${BACKEND}/api/devis/${quote.id}/montant-paye`, { headers: H })
      .then(r => r.json()).then(d => setMontantPaye(d.montant_paye || 0)).catch(() => {});
  // eslint-disable-next-line
  }, [quote?.id]);

  const filteredProds = allProducts.filter(p =>
    !productSearch || p.nom.toLowerCase().includes(productSearch.toLowerCase())
  );

  function addProduct(p) {
    const exists = editedItems.find(i => i.product_id === p.id);
    if (exists) {
      setEditedItems(prev => prev.map(i => i.product_id === p.id ? { ...i, quantite: i.quantite + 1, sous_total: (i.quantite + 1) * i.prix_unitaire } : i));
    } else {
      setEditedItems(prev => [...prev, {
        product_id: p.id, product_nom: p.nom,
        quantite: 1, prix_unitaire: p.tarifs?.particulier || 0,
        sous_total: p.tarifs?.particulier || 0, remise_pct: 0,
        images: p.images || []
      }]);
    }
    setShowAddProduct(false);
    setProductSearch('');
  }

  function addLigneLibre() {
    setEditedItems(prev => [...prev, {
      product_id: null, product_nom: '', product_type: 'libre',
      quantite: 1, prix_unitaire: 0, sous_total: 0, remise_pct: 0, images: [],
    }]);
  }

  function addPhotoboothOption(opt) {
    setEditedItems(prev => [...prev, {
      product_id: null, product_nom: opt.label, product_type: 'photobooth',
      quantite: 1, prix_unitaire: opt.prix, sous_total: opt.prix, remise_pct: 0, images: [],
    }]);
    setShowAddPhotobooth(false);
    toast.success(`${opt.label} ajouté`);
  }

  // Coût des photos imprimées au réel : 0,50 €/photo, plafonné au forfait (= total réel − 150 € location)
  function computePrintCost(n) {
    return photoboothPrintsCost(n);
  }
  function addPrintedPhotos() {
    const n = parseInt(photoCount) || 0;
    if (n <= 0) { toast.error('Indiquez un nombre de photos'); return; }
    const cost = computePrintCost(n);
    setEditedItems(prev => [...prev, {
      product_id: null, product_nom: `Photos imprimées (${n} photos)`, product_type: 'photobooth',
      quantite: 1, prix_unitaire: cost, sous_total: cost, remise_pct: 0, images: [],
    }]);
    setPhotoCount('');
    setShowAddPhotobooth(false);
    toast.success(`${n} photos imprimées ajoutées (${cost.toFixed(2)}€)`);
  }

  function updateItem(idx, key, val) {
    setEditedItems(prev => {
      const items = [...prev];
      items[idx] = { ...items[idx], [key]: val };
      const item = items[idx];
      items[idx].sous_total = parseFloat(item.prix_unitaire || 0) * (item.quantite || 1) * (1 - (item.remise_pct || 0) / 100);
      return items;
    });
  }

  function removeItem(idx) {
    setEditedItems(prev => prev.filter((_, i) => i !== idx));
  }

  function startEditClient() {
    const sess = quote.session || {};
    setClientForm({
      client_prenom: quote.client_prenom || '',
      client_nom: quote.client_nom || '',
      client_email: quote.client_email || '',
      client_telephone: quote.client_telephone || '',
      adresse_livraison: quote.adresse_livraison || sess.adresse || '',
      adresse_facturation: quote.adresse_facturation || '',
      date_evenement: (sess.date_evenement || quote.date_livraison || '').slice(0, 10),
    });
    setEditingClient(true);
  }

  async function saveClient() {
    try {
      const payload = {
        client_prenom: clientForm.client_prenom,
        client_nom: clientForm.client_nom,
        client_email: clientForm.client_email,
        client_telephone: clientForm.client_telephone,
        adresse_livraison: clientForm.adresse_livraison,
        adresse_facturation: clientForm.adresse_facturation,
      };
      if (clientForm.date_evenement) {
        payload.date_livraison = clientForm.date_evenement;
        payload['session.date_evenement'] = clientForm.date_evenement;
      }
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}`, {
        method: 'PUT', headers: H, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      toast.success('Infos client & événement mises à jour');
      setEditingClient(false);
      onSaved();
    } catch {
      toast.error('Échec de la mise à jour');
    }
  }

  const sousTotal = editedItems.reduce((s, i) => s + (i.sous_total || 0), 0);
  const total     = sousTotal * (1 - remisePct / 100);

  async function saveItems() {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/items`, {
        method: 'PATCH', headers: H,
        body: JSON.stringify({ items: editedItems, remise_globale_pct: remisePct, reason: 'Modification admin' })
      });
      if (!res.ok) throw new Error();
      toast.success('Devis mis à jour !');
      onSaved();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  }

  async function addNote() {
    if (!noteTexte.trim()) return;
    await fetch(`${BACKEND}/api/devis/${quote.id}/note-interne`, {
      method: 'POST', headers: H,
      body: JSON.stringify({ texte: noteTexte })
    });
    toast.success('Note ajoutée');
    setNoteTexte('');
    onSaved();
  }

  async function sendToClient() {
    const res = await fetch(`${BACKEND}/api/devis/${quote.id}/envoyer`, { method: 'POST', headers: H });
    const d = await res.json().catch(() => ({}));
    if (d && d.email_sent === false) {
      toast.warning(`Devis marqué envoyé, mais l'email n'est pas parti : ${d.email_error || 'erreur Brevo'}`);
    } else {
      toast.success('Devis envoyé au client !');
    }
    onSaved();
  }

  // ANO-ECC-04 : téléchargement authentifié (Bearer admin) — plus de lien direct par id
  async function openDoc(path) {
    const ok = await openProtectedDoc(BACKEND, path, token);
    if (!ok) toast.error('Lien expiré ou accès refusé. Reconnectez-vous puis réessayez.');
  }
  function downloadDevisPdf() {
    openDoc(`/api/devis/${quote.id}/pdf`);
  }

  async function copyDevisLink() {
    const link = `${window.location.origin}/devis/${quote.id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Lien du devis copié !');
    } catch {
      window.prompt('Copiez le lien du devis :', link);
    }
  }

  async function transformCommande() {
    const res = await fetch(`${BACKEND}/api/devis/${quote.id}/transformer-commande`, { method: 'POST', headers: H });
    const d = await res.json();
    toast.success(`Commande créée : ${d.commande_numero}`);
    onSaved();
  }

  async function cancelQuote() {
    if (!window.confirm(`Annuler le devis ${quote.numero} ?\n\nLe matériel/les prestations seront immédiatement libérés et redeviendront disponibles à la réservation. Cette action peut être faite suite à l'annulation du client.`)) return;
    try {
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/statut?statut=annulee`, { method: 'PUT', headers: H });
      if (!res.ok) throw new Error('Erreur');
      toast.success('Devis annulé — créneaux libérés');
      onSaved();
    } catch { toast.error('Erreur lors de l\'annulation'); }
  }

  async function openCancelRefund() {
    try {
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/annulation-preview`, { headers: H });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setCancelPreview(d);
      setCancelSubject(d.email_subject || '');
      setCancelBody(d.email_body || '');
      setCompEnabled(true);
      setCompMontant(d.compensation_defaut || 15);
      setShowCancelRefund(true);
    } catch { toast.error("Impossible de préparer l'annulation"); }
  }

  async function confirmCancelRefund() {
    setCancelSending(true);
    try {
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/annuler-rembourser`, {
        method: 'POST', headers: H,
        body: JSON.stringify({
          email_subject: cancelSubject, email_body: cancelBody, send_email: true,
          compensation: compEnabled, compensation_montant: compEnabled ? Number(compMontant) || 0 : 0,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Erreur');
      toast.success('Devis annulé & remboursement traité', {
        description: `Carte : ${(d.montant_rembourse_carte || 0).toFixed(2)}€ · Manuel : ${(d.montant_a_rembourser_manuel || 0).toFixed(2)}€ · Compensation offerte : ${(d.compensation_offerte || 0).toFixed(2)}€`,
      });
      setShowCancelRefund(false);
      onSaved();
    } catch (e) { toast.error(`Erreur : ${e.message}`); }
    setCancelSending(false);
  }

  // ── Annulation CLIENT : rembourse tout SAUF la caution (30%), libère les créneaux ──
  async function cancelClientCaution() {
    try {
      const pv = await (await fetch(`${BACKEND}/api/devis/${quote.id}/annulation-client-preview`, { headers: H })).json();
      const ok = window.confirm(
        `Annulation client — devis ${quote.numero}\n\n` +
        `Total payé : ${(pv.total_paye || 0).toFixed(2)}€\n` +
        `Caution conservée (${pv.caution_pct}%) : ${(pv.caution_retenue || 0).toFixed(2)}€\n` +
        `Montant remboursable : ${(pv.montant_rembourse || 0).toFixed(2)}€\n\n` +
        `Une facture des frais d'annulation (caution) sera émise. Les créneaux seront libérés et un email sera envoyé au client. Confirmer ?`
      );
      if (!ok) return;
      let mode_reste = 'rembourser';
      if ((pv.montant_rembourse || 0) > 0) {
        const rembourser = window.confirm(
          `Traitement du montant remboursable (${(pv.montant_rembourse || 0).toFixed(2)}€) :\n\n` +
          `OK = Rembourser le client (carte/virement) + émettre un avoir\n` +
          `Annuler = Émettre un BON D'AVOIR réutilisable à la place`
        );
        mode_reste = rembourser ? 'rembourser' : 'bon_avoir';
      }
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/annuler-client`, {
        method: 'POST', headers: H, body: JSON.stringify({ mode_reste }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Erreur');
      toast.success('Annulation client traitée ✓', {
        description: d.mode_reste === 'bon_avoir'
          ? `Caution : ${(d.caution_retenue || 0).toFixed(2)}€ · Bon d'avoir : ${(d.bon_avoir_montant || 0).toFixed(2)}€ · Facture ${d.facture_caution_numero || ''}`
          : `Caution : ${(d.caution_retenue || 0).toFixed(2)}€ · Remboursé : ${(d.montant_rembourse_total || 0).toFixed(2)}€ · Facture ${d.facture_caution_numero || ''}`,
      });
      onSaved();
    } catch (e) { toast.error(`Erreur : ${e.message}`); }
  }

  // ── Annulation INTEMPÉRIES : bon d'avoir de la TOTALITÉ (aucun remboursement) ──
  async function cancelIntemperies() {
    try {
      const pv = await (await fetch(`${BACKEND}/api/devis/${quote.id}/annulation-intemperies-preview`, { headers: H })).json();
      const ok = window.confirm(
        `Annulation INTEMPÉRIES — devis ${quote.numero}\n\n` +
        `Sommes versées : ${(pv.total_paye || 0).toFixed(2)}€\n` +
        `Bon d'avoir émis (totalité) : ${(pv.montant_bon_avoir || 0).toFixed(2)}€\n\n` +
        `Aucun remboursement : tout est converti en bon d'avoir valable sur une prochaine réservation. Les créneaux seront libérés.\n\nConfirmer ?`
      );
      if (!ok) return;
      const sendEmail = window.confirm("Envoyer aussi l'email de bon d'avoir au client ?\n\nOK = envoyer · Annuler = ne pas envoyer");
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/annuler-intemperies`, {
        method: 'POST', headers: H, body: JSON.stringify({ send_email: sendEmail }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Erreur');
      toast.success('Bon d\'avoir émis (intempéries) ✓', {
        description: `Bon d'avoir : ${(d.montant_bon_avoir || 0).toFixed(2)}€ · Créneaux libérés`,
      });
      onSaved();
    } catch (e) { toast.error(`Erreur : ${e.message}`); }
  }

  // ── Appliquer un bon d'avoir client en remise sur ce devis ──
  async function applyBonAvoirAdmin() {
    try {
      const dispo = await (await fetch(`${BACKEND}/api/devis/${quote.id}/bon-avoir-dispo`, { headers: H })).json();
      if (!dispo || (dispo.applicable || 0) <= 0) {
        toast.info('Aucun bon d\'avoir disponible pour ce client');
        return;
      }
      if (!window.confirm(`Appliquer le bon d'avoir du client en remise ?\n\nMontant déduit : ${(dispo.applicable || 0).toFixed(2)}€\n(débité de son solde, non réutilisable)`)) return;
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/appliquer-bon-avoir`, { method: 'POST', headers: H });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Erreur');
      toast.success('Bon d\'avoir appliqué ✓', { description: `-${(d.montant_applique || 0).toFixed(2)}€ · total ${(d.total || 0).toFixed(2)}€` });
      onSaved();
    } catch (e) { toast.error(`Erreur : ${e.message}`); }
  }

  async function downloadBonAvoirPdf() {
    try {
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/bon-avoir-pdf`, { headers: H });
      if (!res.ok) throw new Error('Attestation indisponible');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { toast.error(`Erreur : ${e.message}`); }
  }

  async function validerDevis() {
    if (!window.confirm(`Valider le devis ${quote.numero} ?\n\nLe devis sera marqué comme validé par l'administration.`)) return;
    try {
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/valider`, { method: 'POST', headers: H });
      if (!res.ok) throw new Error();
      toast.success('Devis validé ✓');
      onSaved();
    } catch { toast.error('Erreur lors de la validation'); }
  }

  async function markPaid(type) {
    const montants = { acompte: total * 0.3, solde: total * 0.7, total: total };
    const montant = montants[type];
    const labels = { acompte: "l'acompte (30%)", solde: 'le solde (70%)', total: 'le total (100%)' };
    const methode = window.prompt(
      `Encaissement manuel de ${labels[type]} — ${montant.toFixed(2)}€\n\nMode de paiement ? (virement, especes, cheque, cb)`,
      'virement'
    );
    if (methode === null) return; // annulé
    // Acompte : reçu envoyé automatiquement. Solde/Total : l'exploitant choisit l'envoi.
    const envoyer_email = type === 'acompte'
      ? true
      : window.confirm('Envoyer la facture par email au client maintenant ?\n\n(OK = envoyer · Annuler = ne pas envoyer)');
    try {
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/paiement-manuel`, {
        method: 'POST', headers: H,
        body: JSON.stringify({ type, methode: (methode || 'virement').trim().toLowerCase(), envoyer_email })
      });
      if (!res.ok) throw new Error();
      toast.success(`Paiement ${type} enregistré ✓`);
      onSaved();
    } catch { toast.error('Erreur lors de l\'enregistrement du paiement'); }
  }

  async function sendSoldePaymentLink() {
    setSendingPayLink(true);
    try {
      const res = await fetch(`${BACKEND}/api/devis/${quote.id}/envoyer-lien-solde`, {
        method: 'POST', headers: H
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success('Lien de paiement du solde envoyé !', {
        description: `Email envoyé à ${quote.client_email}`
      });
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    }
    setSendingPayLink(false);
  }

  const s = STATUTS[quote.statut] || STATUTS.brouillon;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.7)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="w-full max-w-4xl rounded-2xl" style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)' }}>
          {/* Header */}
          <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="font-bold text-xl" style={{ color: 'var(--admin-text)' }}>{quote.numero}</h2>
                <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: s.bg, color: s.color }}>{s.label}</span>
              </div>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {quote.client_nom} · {quote.client_email} · {quote.client_telephone}
              </p>
            </div>
            <button onClick={onClose} style={{ color: 'var(--admin-muted)' }}><X size={20}/></button>
          </div>

          {/* ── Sélecteur d'onglet : Éditer / Cycle de vie ── */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--admin-border-soft)', padding: '0 20px' }}>
            {[
              { k: 'edit',     l: 'Éditer',       icon: <Edit2 size={13}/> },
              { k: 'timeline', l: 'Cycle de vie',  icon: <GitBranch size={13}/> },
            ].map(t => (
              <button key={t.k} onClick={() => setActiveDevisTab(t.k)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '11px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
                  background: 'none', border: 'none',
                  color: activeDevisTab === t.k ? 'var(--admin-text)' : 'var(--admin-muted)',
                  borderBottom: activeDevisTab === t.k ? '2px solid var(--admin-teal)' : '2px solid transparent',
                  marginBottom: -1,
                }}>
                {t.icon} {t.l}
              </button>
            ))}
          </div>

          {/* ── Vue Cycle de vie ── */}
          {activeDevisTab === 'timeline' && (
            <div style={{ padding: 20, maxHeight: '80vh', overflowY: 'auto' }}>
              <AdminOrderTimeline quote={quote} onStatusChange={onReload || onSaved} />
            </div>
          )}

          {/* ── Vue Éditer (DevisEditor — contenu entièrement inchangé) ── */}
          {activeDevisTab === 'edit' && (
          <div className="p-5 grid lg:grid-cols-3 gap-5">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-5">

              {/* Infos client + double adresse */}
              <div className="p-4 rounded-xl grid sm:grid-cols-2 gap-3" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>Client</p>
                    {!editingClient && (
                      <button onClick={startEditClient} data-testid="devis-edit-client-btn"
                        className="flex items-center gap-1 text-xs" style={{ color: 'var(--admin-teal)' }}>
                        <Pencil size={11}/> Modifier
                      </button>
                    )}
                  </div>
                  {editingClient ? (
                    <div className="space-y-1.5" data-testid="devis-client-edit-form">
                      <div className="grid grid-cols-2 gap-1.5">
                        <Input value={clientForm.client_prenom} onChange={e => setClientForm(f => ({ ...f, client_prenom: e.target.value }))} placeholder="Prénom" data-testid="devis-client-prenom" className="h-8 text-xs" />
                        <Input value={clientForm.client_nom} onChange={e => setClientForm(f => ({ ...f, client_nom: e.target.value }))} placeholder="Nom" data-testid="devis-client-nom" className="h-8 text-xs" />
                      </div>
                      <Input type="email" value={clientForm.client_email} onChange={e => setClientForm(f => ({ ...f, client_email: e.target.value }))} placeholder="Email" data-testid="devis-client-email" className="h-8 text-xs" />
                      <Input value={clientForm.client_telephone} onChange={e => setClientForm(f => ({ ...f, client_telephone: e.target.value }))} placeholder="Téléphone" data-testid="devis-client-telephone" className="h-8 text-xs" />
                      <Input value={clientForm.adresse_livraison} onChange={e => setClientForm(f => ({ ...f, adresse_livraison: e.target.value }))} placeholder="Adresse de livraison" data-testid="devis-client-adresse-liv" className="h-8 text-xs" />
                      <Input value={clientForm.adresse_facturation} onChange={e => setClientForm(f => ({ ...f, adresse_facturation: e.target.value }))} placeholder="Adresse de facturation (si différente)" data-testid="devis-client-adresse-fac" className="h-8 text-xs" />
                      <div>
                        <label className="text-xs" style={{ color: 'var(--admin-faint)' }}>Date d'événement</label>
                        <Input type="date" value={clientForm.date_evenement} onChange={e => setClientForm(f => ({ ...f, date_evenement: e.target.value }))} data-testid="devis-client-date-ev" className="h-8 text-xs" />
                      </div>
                      <div className="flex gap-1.5 pt-0.5">
                        <button onClick={saveClient} data-testid="devis-client-save" className="flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold" style={{ background: 'var(--admin-teal)', color: '#04121a' }}>Enregistrer</button>
                        <button onClick={() => setEditingClient(false)} className="px-2 py-1.5 rounded-lg text-xs" style={{ background: 'transparent', color: 'var(--admin-muted)', border: '1px solid var(--admin-border-soft)' }}>Annuler</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }} data-testid="devis-client-name">{[quote.client_prenom, quote.client_nom].filter(Boolean).join(' ') || '—'}</p>
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{quote.client_email || '—'}</p>
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{quote.client_telephone || '—'}</p>
                    </>
                  )}
                </div>
                <div>
                  <p className="text-xs mb-1" style={{ color: 'var(--admin-faint)' }}>Événement</p>
                  <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
                    {quote.session?.type_evenement || '—'} · {quote.session?.date_evenement ? formatDate(quote.session.date_evenement) : '—'}
                  </p>
                </div>
                {/* Adresse de livraison */}
                <div>
                  <p className="text-xs mb-1 flex items-center gap-1" style={{ color: 'var(--admin-teal)' }}>
                    <span>🚚</span> Adresse de livraison
                  </p>
                  <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
                    {quote.adresse_livraison || quote.session?.adresse || 'Non renseignée'}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>Utilisée pour GPS / mission terrain</p>
                </div>
                {/* Adresse de facturation */}
                <div>
                  <p className="text-xs mb-1 flex items-center gap-1" style={{ color: 'var(--admin-gold)' }}>
                    <span>🧾</span> Adresse de facturation
                  </p>
                  {quote.adresse_facturation ? (
                    <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>{quote.adresse_facturation}</p>
                  ) : (
                    <p className="text-xs italic" style={{ color: 'var(--admin-faint)' }}>
                      Identique à la livraison
                    </p>
                  )}
                  <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>Sur contrat & facture</p>
                </div>
              </div>

              {/* Lignes du devis */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--admin-muted)' }}>Articles</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setShowAddProduct(s => !s)} data-testid="devis-edit-add-product"
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                      style={{ background: 'rgba(93,188,191,0.12)', color: 'var(--admin-teal)', border: '1px solid rgba(93,188,191,0.2)' }}>
                      <Plus size={12}/> Ajouter un article
                    </button>
                    <button onClick={addLigneLibre} data-testid="devis-edit-add-ligne-libre"
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                      style={{ background: 'rgba(212,175,55,0.12)', color: 'var(--admin-gold)', border: '1px dashed rgba(212,175,55,0.4)' }}>
                      <Plus size={12}/> Ligne libre
                    </button>
                    <button onClick={() => setShowAddPhotobooth(s => !s)} data-testid="devis-edit-add-photobooth"
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                      style={{ background: 'rgba(147,133,236,0.14)', color: '#B3A7F5', border: '1px solid rgba(147,133,236,0.3)' }}>
                      <Camera size={12}/> Packs photo
                    </button>
                  </div>
                </div>

                {/* Sélecteur de packs photo Photobooth (cochables à la demande du client) */}
                {showAddPhotobooth && (
                  <div className="mb-3 p-3 rounded-xl" data-testid="devis-photobooth-picker"
                    style={{ background: 'var(--admin-surface)', border: '1px solid rgba(147,133,236,0.35)' }}>
                    <p className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color: '#B3A7F5' }}>
                      <Camera size={13}/> Ajouter un pack photo Photobooth
                    </p>
                    <div className="space-y-1.5">
                      {PHOTOBOOTH_DEVIS_OPTIONS.map(opt => (
                        <button key={opt.key} onClick={() => addPhotoboothOption(opt)}
                          data-testid={`devis-photobooth-opt-${opt.key}`}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left hover:bg-white/5"
                          style={{ background: 'transparent', border: '1px solid var(--admin-border-soft)', color: 'var(--admin-text)', cursor: 'pointer' }}>
                          <div className="min-w-0">
                            <span className="text-sm font-medium flex items-center gap-1.5">
                              {opt.label}
                              {opt.popular && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(212,175,55,0.2)', color: 'var(--admin-gold)' }}>Populaire</span>}
                            </span>
                            <span className="text-xs block truncate" style={{ color: 'var(--admin-faint)' }}>{opt.description}</span>
                          </div>
                          <span className="text-xs font-bold ml-2 flex-shrink-0" style={{ color: 'var(--admin-gold)' }}>{opt.prix}€</span>
                        </button>
                      ))}
                    </div>
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--admin-border-soft)' }}>
                      <p className="text-xs font-semibold mb-2" style={{ color: '#B3A7F5' }}>
                        Facturer les photos imprimées au réel (0,50€/photo, plafonné au forfait)
                      </p>
                      <div className="flex items-center gap-2">
                        <input type="number" min="1" value={photoCount}
                          onChange={e => setPhotoCount(e.target.value)}
                          placeholder="Nb de photos" data-testid="devis-photobooth-photocount"
                          className="flex-1 px-2 py-1.5 rounded text-sm"
                          style={{ background: '#222', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
                        <span className="text-sm font-bold" style={{ color: 'var(--admin-gold)', minWidth: 62, textAlign: 'right' }}>
                          {computePrintCost(photoCount).toFixed(2)}€
                        </span>
                        <button onClick={addPrintedPhotos} data-testid="devis-photobooth-add-prints"
                          className="px-3 py-1.5 rounded text-sm font-semibold flex-shrink-0"
                          style={{ background: 'rgba(147,133,236,0.2)', color: '#B3A7F5', border: '1px solid rgba(147,133,236,0.35)', cursor: 'pointer' }}>
                          <Plus size={13} className="inline mr-1"/>Ajouter
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sélecteur de produit */}
                {showAddProduct && (
                  <div className="mb-3 p-3 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', maxHeight: 250, overflowY: 'auto' }}>
                    <Input value={productSearch} onChange={e => setProductSearch(e.target.value)}
                      placeholder="Rechercher un produit..." className="mb-2"
                      style={{ background: '#222', borderColor: 'rgba(255,255,255,0.1)', color: 'var(--admin-text)' }} />
                    {filteredProds.slice(0, 20).map(p => (
                      <button key={p.id} onClick={() => addProduct(p)}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left mb-1 hover:bg-white/5"
                        style={{ background: 'transparent', border: 'none', color: 'var(--admin-text)', cursor: 'pointer' }}>
                        <span className="text-sm">{p.nom}</span>
                        <span className="text-xs" style={{ color: 'var(--admin-gold)' }}>{p.tarifs?.particulier || 0}€</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Tableau lignes */}
                <div className="space-y-2">
                  {editedItems.map((item, idx) => (
                    <div key={idx} className="p-3 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                      <div className="flex items-start gap-3">
                        {item.images?.[0] && (
                          <img src={item.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          {item.product_id == null ? (
                            <input value={item.product_nom} onChange={e => updateItem(idx, 'product_nom', e.target.value)}
                              data-testid={`devis-edit-ligne-nom-${idx}`} placeholder="Nom de l'article (libre)…"
                              className="w-full px-2 py-1 rounded text-sm font-medium mb-2"
                              style={{ background: '#222', border: '1px dashed rgba(212,175,55,0.4)', color: 'var(--admin-text)', outline: 'none' }} />
                          ) : (
                            <p className="text-sm font-medium mb-2" style={{ color: 'var(--admin-text)' }}>{item.product_nom}</p>
                          )}
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="text-xs mb-0.5 block" style={{ color: 'var(--admin-muted)' }}>Qté</label>
                              <input type="number" min="1" value={item.quantite}
                                onChange={e => updateItem(idx, 'quantite', parseInt(e.target.value) || 1)}
                                className="w-full px-2 py-1 rounded text-sm text-center"
                                style={{ background: '#222', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
                            </div>
                            <div>
                              <label className="text-xs mb-0.5 block" style={{ color: 'var(--admin-muted)' }}>P.U. (€)</label>
                              <input type="number" min="0" step="0.01" value={item.prix_unitaire}
                                onChange={e => updateItem(idx, 'prix_unitaire', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 rounded text-sm text-center"
                                style={{ background: '#222', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
                            </div>
                            <div>
                              <label className="text-xs mb-0.5 block" style={{ color: 'var(--admin-muted)' }}>Remise %</label>
                              <input type="number" min="0" max="100" value={item.remise_pct || 0}
                                onChange={e => updateItem(idx, 'remise_pct', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 rounded text-sm text-center"
                                style={{ background: '#222', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
                            </div>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-bold text-sm" style={{ color: 'var(--admin-gold)' }}>{(item.sous_total || 0).toFixed(2)}€</p>
                          <button onClick={() => removeItem(idx)} className="mt-1" style={{ color: '#e53e3e' }}><Trash2 size={13}/></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totaux + Statut paiement */}
                <div className="mt-4 p-4 rounded-xl space-y-2" style={{ background: 'var(--admin-surface)' }}>
                  <div className="flex justify-between text-sm" style={{ color: 'var(--admin-muted)' }}>
                    <span>Sous-total</span><span>{sousTotal.toFixed(2)}€</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: 'var(--admin-muted)' }}>Remise globale (%)</span>
                    <div className="flex items-center gap-2">
                      <input type="number" min="0" max="100" value={remisePct}
                        onChange={e => setRemisePct(parseFloat(e.target.value) || 0)}
                        className="w-20 px-2 py-1 rounded text-sm text-right"
                        style={{ background: '#222', border: '1px solid rgba(212,175,55,0.3)', color: 'var(--admin-gold)', outline: 'none' }} />
                      <span style={{ color: 'var(--admin-gold)' }}>%</span>
                    </div>
                  </div>
                  <div className="flex justify-between font-bold text-base pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', color: 'var(--admin-gold)' }}>
                    <span>TOTAL TTC</span><span>{total.toFixed(2)}€</span>
                  </div>

                  {/* ── Détail acompte / solde avec statut paiement ── */}
                  <div className="mt-3 pt-3 space-y-2" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    {/* Acompte */}
                    <div className="flex items-center justify-between p-2.5 rounded-lg"
                      style={{
                        background: ['acompte_paye','solde_paye','livre','termine'].includes(quote.statut)
                          ? 'rgba(31,157,122,0.1)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${['acompte_paye','solde_paye','livre','termine'].includes(quote.statut) ? 'rgba(31,157,122,0.3)' : 'rgba(255,255,255,0.06)'}`
                      }}>
                      <div className="flex items-center gap-2">
                        {['acompte_paye','solde_paye','livre','termine'].includes(quote.statut)
                          ? <CheckCircle size={13} style={{ color: '#1F9D7A' }} />
                          : <Clock size={13} style={{ color: 'var(--admin-faint)' }} />
                        }
                        <span className="text-xs font-semibold"
                          style={{ color: ['acompte_paye','solde_paye','livre','termine'].includes(quote.statut) ? '#1F9D7A' : 'rgba(255,255,255,0.4)' }}>
                          Acompte 30%
                          {['acompte_paye','solde_paye','livre','termine'].includes(quote.statut) && ' — PAYÉ ✓'}
                        </span>
                      </div>
                      <span className="font-bold text-sm"
                        style={{ color: ['acompte_paye','solde_paye','livre','termine'].includes(quote.statut) ? '#1F9D7A' : '#D4AF37' }}>
                        {(total * 0.3).toFixed(2)}€
                      </span>
                    </div>

                    {/* Solde */}
                    <div className="flex items-center justify-between p-2.5 rounded-lg"
                      style={{
                        background: ['solde_paye','livre','termine'].includes(quote.statut)
                          ? 'rgba(31,157,122,0.1)' : 'rgba(212,175,55,0.06)',
                        border: `1px solid ${['solde_paye','livre','termine'].includes(quote.statut) ? 'rgba(31,157,122,0.3)' : 'rgba(212,175,55,0.2)'}`
                      }}>
                      <div className="flex items-center gap-2">
                        {['solde_paye','livre','termine'].includes(quote.statut)
                          ? <CheckCircle size={13} style={{ color: '#1F9D7A' }} />
                          : <AlertTriangle size={13} style={{ color: 'var(--admin-gold)' }} />
                        }
                        <span className="text-xs font-semibold"
                          style={{ color: ['solde_paye','livre','termine'].includes(quote.statut) ? '#1F9D7A' : '#D4AF37' }}>
                          Solde 70%
                          {['solde_paye','livre','termine'].includes(quote.statut)
                            ? ' — PAYÉ ✓'
                            : quote.statut === 'acompte_paye' ? ' — EN ATTENTE' : ''}
                        </span>
                      </div>
                      <span className="font-bold text-sm"
                        style={{ color: ['solde_paye','livre','termine'].includes(quote.statut) ? '#1F9D7A' : '#D4AF37' }}>
                        {(total * 0.7).toFixed(2)}€
                      </span>
                    </div>

                    {/* Résumé rapide */}
                    {quote.statut === 'acompte_paye' && (
                      <div className="p-2.5 rounded-lg text-center"
                        style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' }}>
                        <p className="text-xs font-bold" style={{ color: 'var(--admin-gold)' }}>
                          💳 Restant à encaisser : <strong>{(total * 0.7).toFixed(2)}€</strong>
                        </p>
                      </div>
                    )}

                    {/* Déjà réglé + solde réel (utile après ajout de photos photobooth) */}
                    {montantPaye > 0 && (
                      <div className="mt-1 pt-2 space-y-1" style={{ borderTop: '1px dashed rgba(255,255,255,0.12)' }}>
                        <div className="flex justify-between text-xs" style={{ color: 'var(--admin-muted)' }}>
                          <span>Déjà réglé</span>
                          <span data-testid="devis-deja-regle" style={{ color: '#1F9D7A', fontWeight: 700 }}>{montantPaye.toFixed(2)}€</span>
                        </div>
                        <div className="flex justify-between text-sm font-bold">
                          <span style={{ color: 'var(--admin-text)' }}>Solde à payer</span>
                          <span data-testid="devis-solde-a-payer"
                            style={{ color: Math.max(total - montantPaye, 0) > 0 ? '#D4AF37' : '#1F9D7A' }}>
                            {Math.max(total - montantPaye, 0).toFixed(2)}€
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Button onClick={saveItems} disabled={saving} className="w-full mt-3" data-testid="devis-edit-save-items"
                  style={{ background: 'rgba(31,157,122,0.2)', color: '#1F9D7A', border: '1px solid rgba(31,157,122,0.3)' }}>
                  <Save size={14} className="mr-2"/> {saving ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
                </Button>
              </div>

              {/* Notes internes */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--admin-muted)' }}>Notes internes</p>
                <div className="flex gap-2 mb-3">
                  <Input value={noteTexte} onChange={e => setNoteTexte(e.target.value)}
                    placeholder="Ajouter une note..."
                    style={{ background: 'var(--admin-surface)', borderColor: 'rgba(255,255,255,0.1)', color: 'var(--admin-text)', flex: 1 }}
                    onKeyDown={e => { if (e.key === 'Enter') addNote(); }} />
                  <Button onClick={addNote} size="sm" style={{ background: 'rgba(125,177,255,0.15)', color: '#7DB1FF' }}>
                    <MessageSquare size={14}/>
                  </Button>
                </div>
                <div className="space-y-2">
                  {(quote.notes_internes || []).map((n, i) => (
                    <div key={i} className="p-3 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(125,177,255,0.1)' }}>
                      <p className="text-sm" style={{ color: 'var(--admin-text)' }}>{n.texte}</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--admin-faint)' }}>
                        {n.auteur} · {formatDateTime(n.date)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cycle de vie — timeline visuelle (déplacée sous les notes internes) */}
              <div data-testid="cycle-de-vie-block">
                <p className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: 'var(--admin-muted)' }}>
                  <GitBranch size={12} /> Cycle de vie
                </p>
                <AdminOrderTimeline quote={quote} onStatusChange={onReload || onSaved} />
              </div>


              {/* Historique */}
              {(quote.versions_history || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--admin-muted)' }}>Historique des versions</p>
                  <div className="space-y-2">
                    {[...quote.versions_history].reverse().map((v, i) => (
                      <div key={i} className="p-3 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                        <div className="flex justify-between">
                          <p className="text-xs font-semibold" style={{ color: 'var(--admin-gold)' }}>Version {v.version}</p>
                          <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{formatDateTime(v.snapshot_at)}</p>
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--admin-muted)' }}>{v.reason} · Total précédent : {v.total}€</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Colonne actions */}
            <div className="space-y-4">
              {/* Compositeur de relances (remplace "Changer le statut") */}
              <RelanceComposer quote={quote} H={H} onSent={onSaved} />

              {/* Historique des relances envoyées */}
              {(quote.relances_log || []).length > 0 && (
                <div className="p-4 rounded-xl" data-testid="relances-history"
                  style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                  <p className="text-xs font-semibold mb-3 flex items-center gap-1.5" style={{ color: 'var(--admin-muted)' }}>
                    <Clock size={12} /> HISTORIQUE DES RELANCES ({(quote.relances_log || []).length})
                  </p>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {[...(quote.relances_log || [])].reverse().map((r, i) => (
                      <div key={i} className="text-xs p-2 rounded-lg"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--admin-border)' }}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-semibold flex items-center gap-1"
                            style={{ color: r.channel === 'sms' ? '#9385EC' : 'var(--admin-teal)' }}>
                            {r.channel === 'sms' ? <MessageSquare size={11} /> : <Mail size={11} />}
                            {r.channel === 'sms' ? 'SMS' : 'Email'}
                            {r.simulated && <span style={{ color: 'var(--admin-amber)', fontWeight: 400 }}>· simulé</span>}
                          </span>
                          <span style={{ color: 'var(--admin-faint)' }}>
                            {r.date ? formatDateTime(r.date) : ''}
                          </span>
                        </div>
                        {r.subject && <p style={{ color: 'var(--admin-text)' }} className="font-medium truncate">{r.subject}</p>}
                        <p style={{ color: 'var(--admin-muted)' }} className="line-clamp-2">{r.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="p-4 rounded-xl space-y-2" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                <p className="text-xs font-semibold mb-3" style={{ color: 'var(--admin-muted)' }}>ACTIONS</p>

                {/* ── Validation admin : valider le devis + paiements manuels + facture ── */}
                {!['annule', 'annulee', 'terminee'].includes(quote.statut) && (
                  <div className="space-y-2 pb-2 mb-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {!['valide_admin', 'acompte_paye', 'solde_paye', 'payee', 'livre', 'livree', 'termine'].includes(quote.statut) && (
                      <button onClick={validerDevis} data-testid="admin-valider-devis-btn"
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                        style={{ background: 'rgba(31,157,122,0.14)', color: '#1F9D7A', border: '1px solid rgba(31,157,122,0.3)' }}>
                        <CheckCircle size={13} /> Valider le devis
                      </button>
                    )}
                    {!['acompte_paye', 'solde_paye', 'payee', 'livre', 'livree', 'termine'].includes(quote.statut) && (
                      <button onClick={() => markPaid('acompte')} data-testid="admin-mark-acompte-btn"
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                        style={{ background: 'rgba(212,175,55,0.14)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.3)' }}>
                        <Wallet size={13} /> Marquer l'acompte payé ({(total * 0.3).toFixed(2)}€)
                      </button>
                    )}
                    {!['solde_paye', 'payee', 'livre', 'livree', 'termine'].includes(quote.statut) && (
                      <button onClick={() => markPaid('solde')} data-testid="admin-mark-solde-btn"
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                        style={{ background: 'rgba(212,175,55,0.14)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.3)' }}>
                        <Wallet size={13} /> Marquer le solde payé ({(total * 0.7).toFixed(2)}€)
                      </button>
                    )}
                    {!['solde_paye', 'payee', 'livre', 'livree', 'termine'].includes(quote.statut) && (
                      <button onClick={() => markPaid('total')} data-testid="admin-mark-total-btn"
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                        style={{ background: 'rgba(31,157,122,0.16)', color: '#1F9D7A', border: '1px solid rgba(31,157,122,0.35)' }}>
                        <CheckCircle size={13} /> Marquer le total réglé ({total.toFixed(2)}€)
                      </button>
                    )}
                    {['acompte_paye', 'solde_paye', 'payee', 'livre', 'livree', 'termine'].includes(quote.statut) && (
                      <button onClick={() => openDoc(`/api/devis/${quote.id}/facture`)} data-testid="admin-voir-facture-btn"
                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                        style={{ background: 'rgba(93,188,191,0.12)', color: 'var(--admin-teal)', border: '1px solid rgba(93,188,191,0.2)', cursor: 'pointer' }}>
                        <Download size={13} /> Voir la facture
                      </button>
                    )}
                  </div>
                )}

                {/* ── Envoyer le devis ── */}
                <button onClick={sendToClient} data-testid="devis-send-client"
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                  style={{ background: 'rgba(93,188,191,0.12)', color: 'var(--admin-teal)', border: '1px solid rgba(93,188,191,0.2)' }}>
                  <Send size={13}/> Envoyer au client
                </button>

                {/* ── PDF + lien (envoi manuel / partage) ── */}
                <div className="flex gap-2">
                  <button onClick={downloadDevisPdf} data-testid="devis-download-pdf"
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(212,175,55,0.12)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.2)' }}>
                    <Download size={13}/> Télécharger PDF
                  </button>
                  <button onClick={copyDevisLink} data-testid="devis-copy-link"
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(147,133,236,0.12)', color: '#B3A7F5', border: '1px solid rgba(147,133,236,0.25)' }}>
                    <Copy size={13}/> Copier le lien
                  </button>
                </div>

                {/* ── Relance devis non signé ── */}
                {['brouillon','envoye','en_attente_signature'].includes(quote.statut) && (
                  <button onClick={async () => {
                    try {
                      const r = await fetch(`${BACKEND}/api/devis/${quote.id}/relance/devis-non-signe`, { method: 'POST', headers: H });
                      const d = await r.json();
                      if (d.success) toast.success('Email "Validez votre devis" envoyé !');
                      else toast.error(d.message || 'Erreur');
                    } catch { toast.error('Erreur envoi'); }
                  }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(212,175,55,0.12)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.2)' }}>
                    <Check size={13}/> Relancer : "Validez votre devis"
                  </button>
                )}

                {/* ── Rappel acompte ── */}
                {['signe','contrat_signe','en_attente_paiement'].includes(quote.statut) && (
                  <button onClick={async () => {
                    try {
                      const r = await fetch(`${BACKEND}/api/devis/${quote.id}/relance/acompte`, { method: 'POST', headers: H });
                      const d = await r.json();
                      if (d.success) toast.success('Rappel acompte envoyé !');
                      else toast.error(d.message || 'Erreur');
                    } catch { toast.error('Erreur envoi'); }
                  }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(197,139,25,0.12)', color: '#C58B19', border: '1px solid rgba(197,139,25,0.2)' }}>
                    <AlertCircle size={13}/> Rappel paiement acompte
                  </button>
                )}

                {/* ── Lien paiement solde ── */}
                {['acompte_paye','contrat_signe'].includes(quote.statut) && (
                  <button onClick={async () => {
                    try {
                      const r = await fetch(`${BACKEND}/api/devis/${quote.id}/relance/solde`, { method: 'POST', headers: H });
                      const d = await r.json();
                      if (d.success) toast.success('Lien paiement solde envoyé !');
                      else toast.error(d.message || 'Erreur');
                    } catch { toast.error('Erreur envoi'); }
                  }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(31,157,122,0.12)', color: '#1F9D7A', border: '1px solid rgba(31,157,122,0.2)' }}>
                    <CreditCard size={13}/> Envoyer lien solde ({(total * 0.7).toFixed(2)}€)
                  </button>
                )}

                {/* ── Rappels livraison ── */}
                {['acompte_paye','solde_paye','a_preparer','produits_charges'].includes(quote.statut) && (
                  <>
                    <button onClick={async () => {
                      try {
                        const r = await fetch(`${BACKEND}/api/devis/${quote.id}/relance/livraison-j2`, { method: 'POST', headers: H });
                        const d = await r.json();
                        if (d.success) toast.success('Rappel J-2 envoyé !');
                        else toast.error('Erreur');
                      } catch { toast.error('Erreur envoi'); }
                    }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                      style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6', border: '1px solid rgba(59,130,246,0.2)' }}>
                      <Truck size={13}/> Rappel livraison J-2
                    </button>
                    <button onClick={async () => {
                      try {
                        const r = await fetch(`${BACKEND}/api/devis/${quote.id}/relance/livraison-j1`, { method: 'POST', headers: H });
                        const d = await r.json();
                        if (d.success) toast.success('Rappel J-1 envoyé !');
                        else toast.error('Erreur');
                      } catch { toast.error('Erreur envoi'); }
                    }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                      style={{ background: 'rgba(139,92,246,0.12)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.2)' }}>
                      <Calendar size={13}/> Rappel livraison J-1
                    </button>
                    <button onClick={async () => {
                      try {
                        const r = await fetch(`${BACKEND}/api/devis/${quote.id}/relance/livraison-jour-j`, { method: 'POST', headers: H });
                        const d = await r.json();
                        if (d.success) toast.success('Rappel Jour J envoyé !');
                        else toast.error('Erreur');
                      } catch { toast.error('Erreur envoi'); }
                    }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                      style={{ background: 'rgba(249,197,209,0.12)', color: '#F9C5D1', border: '1px solid rgba(249,197,209,0.2)' }}>
                      <Zap size={13}/> Rappel Jour J (+ SMS)
                    </button>
                  </>
                )}

                {/* ── Ancien bouton solde (gardé pour rétro-compatibilité mais caché si nouveau bouton présent) ── */}
                {quote.statut === 'acompte_paye' && false && (
                  <button onClick={sendSoldePaymentLink} disabled={sendingPayLink}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(212,175,55,0.15)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.3)', cursor: sendingPayLink ? 'wait' : 'pointer' }}>
                    <CreditCard size={13}/>
                    {sendingPayLink ? 'Envoi en cours...' : `Envoyer lien solde (${(total * 0.7).toFixed(2)}€)`}
                  </button>
                )}

                {/* ── Contrat ── */}
                <button onClick={() => openDoc(`/api/contrat/${quote.id}`)} data-testid="admin-voir-contrat-btn"
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                  style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981', border: '1px solid rgba(16,185,129,0.2)', cursor: 'pointer' }}>
                  <FileText size={13}/> Voir le contrat
                </button>

                {/* ── Fiche de mission ── */}
                {['a_preparer','produits_charges','en_livraison','livree','en_location',
                  'retour_a_effectuer','payee','contrat_signe',
                  'solde_paye','valide_admin','confirme','acompte_paye'].includes(quote.statut) && (
                  <a href={`/mission?quote=${quote.id}`} target="_blank" rel="noreferrer"
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6', border: '1px solid rgba(59,130,246,0.2)', textDecoration: 'none', display: 'flex' }}>
                    <ArrowRight size={13}/> Fiche de mission terrain
                  </a>
                )}

                <button onClick={downloadDevisPdf} data-testid="admin-devis-pdf-btn"
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                  style={{ background: 'rgba(212,175,55,0.1)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.2)', cursor: 'pointer' }}>
                  <Download size={13}/> Télécharger PDF
                </button>
                {['signe','acompte_paye','solde_paye','payee','contrat_signe'].includes(quote.statut) && !quote.commande_id && (
                  <button onClick={transformCommande}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(139,92,246,0.12)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.2)' }}>
                    <ArrowRight size={13}/> Transformer en commande
                  </button>
                )}

                {/* ── Annuler (suite à annulation client) ── */}
                {!['annule', 'annulee', 'terminee'].includes(quote.statut) && (
                  <button onClick={cancelQuote} data-testid="cancel-quote-btn"
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(217,75,93,0.12)', color: '#D94B5D', border: '1px solid rgba(217,75,93,0.25)' }}>
                    <X size={13}/> Annuler (libère les créneaux)
                  </button>
                )}

                {/* ── Annuler & rembourser (double réservation) ── */}
                {!['annule', 'annulee', 'terminee'].includes(quote.statut) && (
                  <button onClick={openCancelRefund} data-testid="cancel-refund-btn"
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: '#D94B5D', color: '#fff', border: '1px solid #D94B5D' }}>
                    <AlertTriangle size={13}/> Annuler & rembourser (double résa)
                  </button>
                )}

                {/* ── Annulation client : rembourser sauf caution (30%) ── */}
                {!['annule', 'annulee', 'terminee'].includes(quote.statut) && (
                  <button onClick={cancelClientCaution} data-testid="cancel-client-caution-btn"
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(244,167,178,0.14)', color: '#F4A7B2', border: '1px solid rgba(244,167,178,0.3)' }}>
                    <RotateCw size={13}/> Annuler (client) — rembourser sauf caution 30%
                  </button>
                )}

                {/* ── Annulation intempéries : bon d'avoir de la totalité ── */}
                {!['annule', 'annulee', 'terminee'].includes(quote.statut) && (
                  <button onClick={cancelIntemperies} data-testid="cancel-intemperies-btn"
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(96,165,250,0.14)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.3)' }}>
                    <AlertTriangle size={13}/> Annuler (intempéries) — bon d'avoir total
                  </button>
                )}

                {/* ── Appliquer un bon d'avoir client en remise ── */}
                {!['annule', 'annulee', 'terminee'].includes(quote.statut) && (
                  <button onClick={applyBonAvoirAdmin} data-testid="apply-bon-avoir-admin-btn"
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(96,165,250,0.1)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.25)' }}>
                    <Tag size={13}/> Appliquer un bon d'avoir (remise)
                  </button>
                )}

                {/* ── Attestation PDF du bon d'avoir (si annulé intempéries) ── */}
                {(quote.annulation?.motif === 'intemperies') && (
                  <button onClick={downloadBonAvoirPdf} data-testid="bon-avoir-pdf-admin-link"
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: 'rgba(96,165,250,0.1)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.25)' }}>
                    <Download size={13}/> Attestation bon d'avoir (PDF)
                  </button>
                )}
              </div>

              {/* ── Modal : Annuler & rembourser ── */}
              {showCancelRefund && cancelPreview && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
                  style={{ background: 'rgba(0,0,0,0.65)' }} data-testid="cancel-refund-modal"
                  onClick={() => !cancelSending && setShowCancelRefund(false)}>
                  <div className="rounded-2xl w-full max-w-lg"
                    style={{ background: '#161A1D', border: '1px solid var(--admin-border-soft)', maxHeight: '92vh', overflowY: 'auto' }}
                    onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-start justify-between p-5" style={{ borderBottom: '1px solid var(--admin-border-soft)' }}>
                      <div className="flex items-center gap-2">
                        <AlertTriangle size={18} style={{ color: '#D94B5D' }} />
                        <div>
                          <h3 className="font-bold text-base" style={{ color: 'var(--admin-text)' }}>Annuler & rembourser</h3>
                          <p className="text-xs mt-0.5" style={{ color: 'var(--admin-muted)' }}>Devis {quote.numero} — double réservation</p>
                        </div>
                      </div>
                      <button onClick={() => !cancelSending && setShowCancelRefund(false)} disabled={cancelSending} data-testid="cancel-refund-close"
                        className="p-1 rounded-lg" style={{ color: 'var(--admin-muted)' }}><X size={18} /></button>
                    </div>

                    <div className="p-5 space-y-4">
                      {/* Récap remboursement */}
                      <div className="rounded-xl p-3 space-y-1.5" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                        <div className="flex justify-between text-xs">
                          <span style={{ color: 'var(--admin-muted)' }}>Remboursement carte (Stripe, automatique)</span>
                          <span className="font-bold" style={{ color: '#1F9D7A' }}>{(cancelPreview.montant_carte || 0).toFixed(2)}€</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span style={{ color: 'var(--admin-muted)' }}>À rembourser manuellement</span>
                          <span className="font-bold" style={{ color: 'var(--admin-gold)' }}>{(cancelPreview.montant_manuel || 0).toFixed(2)}€</span>
                        </div>
                        <div className="flex justify-between text-xs pt-1.5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <span style={{ color: 'var(--admin-text)' }}>Total encaissé</span>
                          <span className="font-bold" style={{ color: 'var(--admin-text)' }}>{(cancelPreview.total_paye || 0).toFixed(2)}€</span>
                        </div>
                      </div>

                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                        Le matériel reste <strong>bloqué à cette date</strong> (déjà réservé par un autre client). Cet email sera envoyé à <strong>{cancelPreview.client_email || 'client'}</strong>.
                      </p>

                      {/* Compensation (bon offert crédité sur la cagnotte fidélité) */}
                      <div className="rounded-xl p-3" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(212,175,55,0.25)' }}>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={compEnabled} onChange={e => setCompEnabled(e.target.checked)} data-testid="cancel-refund-comp-toggle" />
                          <span className="text-xs font-semibold" style={{ color: 'var(--admin-gold)' }}>Offrir une compensation (créditée sur la cagnotte du client)</span>
                        </label>
                        {compEnabled && (
                          <div className="flex items-center gap-2 mt-2">
                            <input type="number" min="0" step="1" value={compMontant} onChange={e => setCompMontant(e.target.value)} data-testid="cancel-refund-comp-montant"
                              className="w-24 px-2 py-1.5 rounded-lg text-sm"
                              style={{ background: 'var(--admin-surface2, #222)', border: '1px solid var(--admin-border-soft)', color: 'var(--admin-text)' }} />
                            <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>€ offerts — utilisables sur une prochaine location (sans expiration).</span>
                          </div>
                        )}
                      </div>

                      {/* Email éditable */}
                      <div>
                        <label className="text-xs font-semibold" style={{ color: 'var(--admin-muted)' }}>Objet</label>
                        <input value={cancelSubject} onChange={(e) => setCancelSubject(e.target.value)} data-testid="cancel-refund-subject"
                          className="w-full mt-1 px-3 py-2 rounded-lg text-sm"
                          style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)', color: 'var(--admin-text)' }} />
                      </div>
                      <div>
                        <label className="text-xs font-semibold" style={{ color: 'var(--admin-muted)' }}>Message (modifiable)</label>
                        <textarea value={cancelBody} onChange={(e) => setCancelBody(e.target.value)} data-testid="cancel-refund-body" rows={8}
                          className="w-full mt-1 px-3 py-2 rounded-lg text-sm"
                          style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)', color: 'var(--admin-text)', resize: 'vertical' }} />
                      </div>
                    </div>

                    <div className="p-5 flex gap-2" style={{ borderTop: '1px solid var(--admin-border-soft)' }}>
                      <button onClick={() => setShowCancelRefund(false)} disabled={cancelSending}
                        className="flex-1 px-3 py-2.5 rounded-lg text-xs font-semibold"
                        style={{ background: 'var(--admin-surface)', color: 'var(--admin-muted)', border: '1px solid var(--admin-border-soft)' }}>
                        Annuler
                      </button>
                      <button onClick={confirmCancelRefund} disabled={cancelSending} data-testid="cancel-refund-confirm"
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-bold"
                        style={{ background: '#D94B5D', color: '#fff', opacity: cancelSending ? 0.6 : 1 }}>
                        <Send size={13} /> {cancelSending ? 'Traitement…' : 'Rembourser & envoyer'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Récapitulatif paiement ── */}
              <div className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                <p className="text-xs font-semibold mb-3" style={{ color: 'var(--admin-muted)' }}>ÉTAT DES PAIEMENTS</p>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>Total devis</span>
                    <span className="font-bold text-sm" style={{ color: 'var(--admin-gold)' }}>{total.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1.5">
                      {['acompte_paye','solde_paye','livre','termine'].includes(quote.statut)
                        ? <CheckCircle size={11} style={{ color: '#1F9D7A' }} />
                        : <Clock size={11} style={{ color: 'var(--admin-faint)' }} />}
                      <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>Acompte encaissé</span>
                    </div>
                    <span className="text-xs font-semibold"
                      style={{ color: ['acompte_paye','solde_paye','livre','termine'].includes(quote.statut) ? '#1F9D7A' : 'rgba(255,255,255,0.3)' }}>
                      {['acompte_paye','solde_paye','livre','termine'].includes(quote.statut) ? `+${(total * 0.3).toFixed(2)}€` : '0€'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1.5">
                      {['solde_paye','livre','termine'].includes(quote.statut)
                        ? <CheckCircle size={11} style={{ color: '#1F9D7A' }} />
                        : <AlertTriangle size={11} style={{ color: quote.statut === 'acompte_paye' ? '#D4AF37' : 'rgba(255,255,255,0.3)' }} />}
                      <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>Solde restant</span>
                    </div>
                    <span className="text-xs font-bold"
                      style={{ color: ['solde_paye','livre','termine'].includes(quote.statut) ? '#1F9D7A' : '#D4AF37' }}>
                      {['solde_paye','livre','termine'].includes(quote.statut) ? '0€ ✓' :
                       ['acompte_paye'].includes(quote.statut) ? `${(total * 0.7).toFixed(2)}€` : `${total.toFixed(2)}€`}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ color: 'var(--admin-faint)' }}>Date événement</span>
                    <span style={{ color: 'var(--admin-text)' }}>{quote.session?.date_evenement || quote.adresse_livraison ? formatDate(quote.session?.date_evenement || '') || '—' : '—'}</span>
                  </div>
                </div>
              </div>

              {/* Infos devis */}
              <div className="p-4 rounded-xl space-y-2" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--admin-muted)' }}>INFOS DEVIS</p>
                {[
                  { label: 'N° devis',     value: quote.numero },
                  { label: 'Créé le',      value: quote.created_at ? formatDate(quote.created_at) : '—' },
                  { label: 'Version',      value: `v${quote.version || 1}` },
                  { label: 'Acompte',      value: `${quote.acompte || 0}€ (30%)` },
                  { label: 'Solde',        value: `${quote.solde || 0}€ (70%)` },
                  { label: 'Profil',       value: quote.client_profile || 'particulier' },
                ].map((r, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span style={{ color: 'var(--admin-muted)' }}>{r.label}</span>
                    <span style={{ color: 'var(--admin-text)', fontWeight: 500 }}>{r.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          )} {/* fin activeDevisTab === 'edit' */}
        </div>
      </div>
    </div>
  );
}
